import styled, { useTheme } from 'styled-components';
import { useCallback, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';

import { OptionList, Typography, useStatusInfo } from '@libs/components';
import {
  CurrencyType,
  TransactionStatusType,
  TransactionType,
} from '@libs/types';
import { formatDate } from '@libs/hooks';
import {
  convertExponentialToDecimal,
  formatBalance,
  formatCryptoAmount,
} from '@libs/helpers';

import { getTransactionDetailsQuery } from 'src/gql';
import { useApiResponse, useModal, useTranslation } from 'src/hooks';
import { getAlternativeAssetLabel } from 'src/helpers';
import {
  TRANSACTION_LABELS,
  TRANSACTION_OPERATIONS_TITLE,
  TRANSACTION_STATUS_FILTER_ICONS,
  USDT_PRICE,
} from 'src/constants';
import { DASHBOARD_NAV_WIDTH } from 'src/components/dashboard-nav/DashboardNav';
import { InfoFieldDescription, TransactionDetailsHeader } from 'src/components';

export const useTransactionsModal = (modalLeftSpace = DASHBOARD_NAV_WIDTH) => {
  const { Dialog, openModal } = useModal();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { getStatusData } = useStatusInfo();

  const [transactionDetails, { data: DetailsData, loading }] = useLazyQuery(
    getTransactionDetailsQuery
  );

  const { response } = useApiResponse({
    data: DetailsData,
    loading,
  });

  const data = useMemo(
    () => response?.getTransactionDetails?.[0]?.details,
    [response]
  );

  const formattedRate =
    data?.rate && data?.rate > 1
      ? formatBalance(convertExponentialToDecimal(data?.rate))
      : convertExponentialToDecimal(data?.rate || 0);

  const handleTransactionDetails = useCallback(
    (index: number) => {
      transactionDetails({
        variables: { record: { transactionId: index } },
      });
    },
    [transactionDetails]
  );

  const modifiedDate = useMemo(() => {
    const date = new Date(data?.date || '');

    return formatDate('D MMM YYYY HH:mm', date);
  }, [data]);

  const infoFields = useMemo(() => {
    const tempData = [];
    const transactionStatus =
      data?.status === TransactionStatusType.DRAFT
        ? getStatusData(TransactionStatusType.PENDING)
        : getStatusData(data?.status);

    const shouldCopyButtonHide = [
      CurrencyType.GEL,
      CurrencyType.EUR,
      CurrencyType.USD,
    ].includes(data?.assetCode as CurrencyType);

    const amount = data?.amount || 0;

    const amountToShow =
      TransactionType.BonusClaim === data?.type
        ? formatCryptoAmount(amount, USDT_PRICE, 2)
        : amount;

    if (data) {
      data.type &&
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('table_status')}
            </Typography>
          ),
          description: (
            <FlexContainer>
              {TRANSACTION_STATUS_FILTER_ICONS[data?.status]}
              <Typography
                type="subHeadline"
                fontFamily="primaryMedium"
                color={transactionStatus.colors[0]}
              >
                {t(transactionStatus.text)}
              </Typography>
            </FlexContainer>
          ),
        });
      tempData.push({
        title: (
          <Typography
            type="bodyButton"
            fontFamily="primaryRegular"
            color={colors.text.secondary}
          >
            {t('transaction_invoice_id')}
          </Typography>
        ),
        description: (
          <Typography type="bodyButton" fontFamily="primaryMedium">
            {data.internalOperationId}
          </Typography>
        ),
      });
      tempData.push({
        title: (
          <Typography
            type="bodyButton"
            fontFamily="primaryRegular"
            color={colors.text.secondary}
          >
            {t('transaction_invoice_time')}
          </Typography>
        ),
        description: (
          <Typography type="bodyButton" fontFamily="primaryMedium">
            {modifiedDate}
          </Typography>
        ),
      });
      tempData.push({
        title: (
          <Typography
            type="bodyButton"
            fontFamily="primaryRegular"
            color={colors.text.secondary}
          >
            {t('transaction_invoice_amount')}
          </Typography>
        ),
        description: (
          <Typography type="bodyButton" fontFamily="primaryMedium">
            {`${amountToShow} ${getAlternativeAssetLabel(data.assetCode)}`}
          </Typography>
        ),
      });

      const shouldShowTransactionDestination =
        !!data.destination && data.type != TransactionType.Deposit;

      !!data.sourceAddress &&
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('common_source_address')}
            </Typography>
          ),
          description: (
            <InfoFieldDescription
              shouldCopyButtonHide={shouldCopyButtonHide}
              shouldShorten={true}
              data={data.sourceAddress as string}
            />
          ),
        });

      if (shouldShowTransactionDestination) {
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_invoice_destination')}
            </Typography>
          ),
          description: (
            <InfoFieldDescription
              shouldCopyButtonHide={shouldCopyButtonHide}
              shouldShorten={true}
              data={data.destination as string}
            />
          ),
        });
      }

      !!data.blockchainTransactionId &&
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_invoice_blockchain_id')}
            </Typography>
          ),
          description: (
            <InfoFieldDescription
              data={data.blockchainTransactionId as string}
            />
          ),
        });
      !!data.network &&
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_invoice_network')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${data.network}`}
            </Typography>
          ),
        });
      !!data.quoteAmount &&
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {data.type === TransactionType.Buy
                ? t('transaction_invoice_price')
                : t('transaction_invoice_received_amount')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${data.quoteAmount} ${getAlternativeAssetLabel(
                data.quoteAssetCode
              )}`}
            </Typography>
          ),
        });
      !!data.fee &&
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_invoice_fee')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${data.fee} ${getAlternativeAssetLabel(data.feeAssetCode)}`}
            </Typography>
          ),
        });

      !!data.rate &&
        tempData.push({
          title: (
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.secondary}
            >
              {t('transaction_invoice_rate')}
            </Typography>
          ),
          description: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`1 ${getAlternativeAssetLabel(
                data.assetCode
              )} ≈ ${formattedRate} ${getAlternativeAssetLabel(
                data.quoteAssetCode
              )}`}
            </Typography>
          ),
        });
    }

    return tempData;
  }, [data, getStatusData, t, modifiedDate, formattedRate, colors]);

  const transactionModal = useMemo(() => {
    if (!data) return;

    return (
      <Dialog
        leftSpace={modalLeftSpace}
        titleIcon={<AssetIcon src={data.assetIconUrl} />}
        title={data.assetLabel}
      >
        <TransactionDetails>
          <TransactionDetailsHeader
            assetIcone={data.fromAsset?.iconUrl ?? data.assetIconUrl}
            toAssetIcone={data.toAsset?.iconUrl}
            title={t(TRANSACTION_LABELS[data.type])}
            amount={
              data.fromAmount
                ? data.fromAmount.toString()
                : data.amount.toString()
            }
            assetCode={getAlternativeAssetLabel(
              (data.fromAsset?.code ?? data.assetCode) as CurrencyType
            )}
            operationTitle={t(TRANSACTION_OPERATIONS_TITLE[data.type])}
            toAssetCode={getAlternativeAssetLabel(
              data.toAsset?.code as CurrencyType
            )}
            toAmount={data.toAmount}
          />
          {/* <StatusWrapper>
            <Typography
              type="subHeadline"
              fontFamily="primaryBold"
              color={transactionStatus.colors[0]}
            >
              {t(transactionStatus.text)}
            </Typography>
          </StatusWrapper> */}
          <CustomOptionList options={infoFields} fontType="bodyButton" />
          {/* TODO: implement PDF download */}
          {/* <DownloadButton title={t('transaction_receipt')} /> */}
        </TransactionDetails>
      </Dialog>
    );
  }, [Dialog, infoFields, data, modalLeftSpace, t]);

  return {
    transactionModal,
    openModal,
    handleTransactionDetails,
  };
};
const AssetIcon = styled.img`
  width: 3rem;
  height: 3rem;
`;
const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
`;
const StatusWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 3rem;
  margin-bottom: 2.7rem;
  border-radius: ${({ theme }) => theme.borderRadius.highL};
  background-color: ${({ theme }) =>
    theme.colors.interactive.secondaryDisabled};
`;

const CustomOptionList = styled(OptionList)`
  width: 100%;
  padding: 0;
  margin-bottom: 0;
  border: none;
  padding-left: 1.6rem;
  .text-wrapper {
    /* width: unset; */
  }
  .description-wrapper {
    padding-right: 1.6rem;
  }
  .description {
    width: max-content;
  }
`;
const TransactionDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.8rem 2.2rem 3.2rem;
  max-height: 50rem;

  ${({ theme }) => theme.media.tablet`
   max-height: initial;
  `};

  ${({ theme }) => theme.media.laptop`
    max-width: 50rem;
    padding: 2.8rem 1.6rem 2.4rem;
    min-width: 42rem;
  `};
`;
